import { type JSX, useEffect, useState } from 'react';

import { buchbare, type Terminauswahl } from '../api/termine';

/* Die Uhr am Standort (FRS-01), wie in der Angebotsakte. */
function zeitpunkt(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Berlin',
  });
}

/**
 * L5 (CRM-116): der offene Termin einer Einzelplatzzeile — am Auftrag, am
 * Abruf aus dem Rahmenauftrag und beim Nachtragen an einer Zeile mit
 * „Termin offen". Dieselbe Auswahl wie im Angebot (ANG-29): nur buchbare
 * offene Termine des Produkts, mit der freien Kapazität aus der Sicht des
 * Kunden (KAP-04) und gefiltert nach seiner Kundenart (BUC-105).
 *
 * Die Auswahl ist eine Auskunft. Ob der Platz da ist, entscheidet der Server
 * unter Sperre (BUC-06) — mit der Option sofort, sonst mit der Bestätigung.
 */
export function Platzterminwahl({
  id,
  artikel,
  kunde,
  kundenart,
  menge,
  wert,
  beiWahl,
  ausser,
}: {
  readonly id: string;
  readonly artikel: string;
  readonly kunde: string;
  readonly kundenart?: string;
  /** Wie viele Plätze die Zeile meint — ein Termin mit weniger freien wird markiert. */
  readonly menge: number;
  readonly wert: string;
  readonly beiWahl: (termin: string) => void;
  /** Der Termin, auf dem die Zeile schon steht; er steht nicht noch einmal zur Wahl. */
  readonly ausser?: string;
}): JSX.Element {
  const [termine, setzeTermine] = useState<readonly Terminauswahl[]>([]);
  const [fehler, setzeFehler] = useState(false);

  useEffect(() => {
    let verworfen = false;
    buchbare(artikel, ausser, kunde, kundenart)
      .then((liste) => {
        if (verworfen) return;
        setzeTermine(liste);
        setzeFehler(false);
      })
      .catch(() => {
        if (verworfen) return;
        setzeTermine([]);
        /* ERR-01: „keiner da" und „nicht erreichbar" sind zweierlei. */
        setzeFehler(true);
      });
    return () => {
      verworfen = true;
    };
  }, [artikel, ausser, kunde, kundenart]);

  const gewaehlt = termine.find((t) => t.id === wert);
  const zuKnapp = gewaehlt !== undefined && gewaehlt.frei < menge;

  return (
    <div className="feld">
      <label className="feld__beschriftung" htmlFor={id}>
        Termin
      </label>
      <select id={id} value={wert} onChange={(e) => beiWahl(e.target.value)}>
        <option value="">— Termin offen</option>
        {termine.map((t) => (
          <option key={t.id} value={t.id}>
            {`${zeitpunkt(t.beginn)} Uhr · ${String(t.frei)} von `
              + `${String(t.hoechstteilnehmer)} frei`}
          </option>
        ))}
      </select>
      <p className="feld__hilfe" role={zuKnapp ? 'alert' : undefined}>
        {fehler
          ? 'Die Terminauskunft antwortet nicht. Legen Sie die Position mit offenem Termin '
            + 'an und tragen Sie ihn nach.'
          : termine.length === 0
            ? 'Für dieses Produkt gibt es keinen buchbaren Termin. Der Termin bleibt offen.'
            : zuKnapp
              ? `Auf diesem Termin sind nur noch ${String(gewaehlt.frei)} Plätze frei — `
                + `die Zeile meint ${String(menge)}. Die Bestätigung würde abgewiesen.`
              : wert === ''
                ? 'Ohne Termin werden keine Plätze gebucht. Er lässt sich bis zur '
                  + 'Bestätigung nachtragen.'
                : 'Gebucht wird mit der Bestätigung des Auftrags. Wer die Plätze '
                  + 'schon jetzt zurücklegen will, nennt eine Frist.'}
      </p>
    </div>
  );
}
